import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '@/lib/api';
import { formatDue, taskKindLabel, viewerZone } from '@/lib/task-time';
import type { Task, TaskBrief } from '@/lib/types';
import { useAuth } from '@/lib/use-auth';

/**
 * Client-side task reminders, off `/tasks/brief` and nothing else.
 *
 * ⚠️ THE LIMITATION THIS HOOK CANNOT FIX. There is no push, no websocket and no
 * service worker in this app, so **nothing fires with the tab closed**. Every
 * surface built on this hook has to say so — a reminder people learn not to
 * trust is worse than no reminder at all. `TAB_OPEN_CAVEAT` is here so that
 * copy has one home.
 *
 * Three rules shape the implementation, and each exists because the obvious
 * version is wrong:
 *
 *  - **The server's clock decides, not the laptop's.** `brief.now` is diffed
 *    against `Date.now()` into a skew, and every comparison and every timer
 *    goes through it. Office machines are routinely minutes out; without the
 *    correction a reminder fires that far early or late, which is exactly the
 *    failure that makes people stop believing the chime.
 *  - **`setTimeout` cannot hold more than ~24.8 days** (2147483647 ms). Past
 *    that it fires *immediately*, so a task a month out would ring the moment
 *    it loaded. Anything beyond the cap is left unscheduled and picked up by a
 *    later poll instead.
 *  - **Reconciliation is by task id.** Polls overlap with armed timers and
 *    re-deliver the same task; `firedRef` survives every re-poll, so nothing
 *    rings twice.
 *  - **One chime is a chime that gets missed.** A reminder repeats its sound
 *    every `CHIME_REPEAT_MS` until it is dismissed, and falls silent after
 *    `CHIME_REPEAT_LIMIT_MS` — the SOUND stops there, never the toast. One
 *    shared ticker serves every live reminder, so three tasks falling due
 *    together are three repeating reminders and still exactly one chime per
 *    tick.
 *  - **A backlog is not the same thing as a missed reminder.** The first brief
 *    of a session seeds anything already past as fired without ringing — but
 *    only past `CATCH_UP_WINDOW_MS`. Something that fell due minutes ago, while
 *    the tab was shut, still surfaces once, batched into a single toast, a
 *    single chime and a single notification however many there are.
 *
 * The brief is always the CALLER's own queue — a task set for a colleague is
 * not in it and must never ring here. That is why this reads `/tasks/brief`
 * and never `GET /tasks`.
 */

/** One short line of copy, said wherever reminders are configured. */
export const TAB_OPEN_CAVEAT = 'Reminders only run while SEED EMERGE is open in a tab.';

/** Past this, `setTimeout` overflows to a zero delay and fires at once. */
const MAX_TIMEOUT_MS = 2_147_483_647;

/*
 * The poll window. Jittered inside it so that two tabs, or two people behind
 * one office NAT, do not line up: the limit is per IP, shared by the whole
 * office, so synchronised tabs arrive as one burst rather than as a spread.
 * ⚠️ *SUPERSEDED: "the limit is 120 requests per 60 seconds per IP."* It is
 * **12,000/60s** (corrected 2026-09-17). Jitter is still right — a fleet of
 * tabs polling in lockstep is the wrong shape at any ceiling — but the figure
 * that reason cited is off by a factor of a hundred.
 */
const POLL_MIN_MS = 60_000;
const POLL_MAX_MS = 120_000;
const MAX_BACKOFF_MS = 600_000;

/** A tab hidden for longer than this re-reads on the way back. */
const REFRESH_AFTER_HIDDEN_MS = 60_000;

/** Older toasts fall off the stack; an unattended tab must not grow forever. */
const MAX_TOASTS = 3;

/*
 * THE REPEATING CHIME. Both numbers are the owner's decision, not a guess: a
 * reminder that rang once was being missed, so it rings again every
 * CHIME_REPEAT_MS until it is dismissed, and gives up after
 * CHIME_REPEAT_LIMIT_MS.
 *
 * The limit stops the SOUND ONLY. The toast stays on screen until someone
 * dismisses it — a reminder that removed itself would be worse than one that
 * went quiet, because the quiet one is still readable.
 */
const CHIME_REPEAT_MS = 20_000;
const CHIME_REPEAT_LIMIT_MS = 5 * 60_000;

/**
 * The test reminder repeats down the same path, so the owner can hear the
 * behaviour without waiting for a real task — but it gives up after one minute
 * (three repeats). It is a diagnostic, not an alarm, and a five-minute alarm
 * fired from a settings panel is a button people learn not to press.
 */
const TEST_CHIME_REPEAT_LIMIT_MS = 60_000;

/**
 * THE BACKLOG THRESHOLD. How far past due still counts as "this just came due"
 * rather than "this is old work".
 *
 * A task that went overdue inside this window while nobody was watching — the
 * tab shut, the laptop asleep, the page reloaded — is surfaced once when the
 * session opens. Anything older than it is seeded silently, because signing in
 * on a Monday with eleven tasks from last week must not play eleven chimes;
 * the badge is the right surface for a backlog.
 *
 * Fifteen minutes is the span in which a missed reminder is still ACTIONABLE:
 * long enough to cover a reload, a meeting or a lunch, short enough that
 * nothing rings about work you have already moved on from.
 */
const CATCH_UP_WINDOW_MS = 15 * 60_000;

/*
 * A two-day horizon, not the default week. Everything that could possibly ring
 * before the next poll is inside it, the counts stay exact whatever it is set
 * to, and a smaller response is cheaper on a shared rate limit.
 */
const BRIEF_PARAMS = { horizonDays: 2, limit: 200 } as const;

const ENABLED_KEY = 'emerge.reminders.enabled';
const CHIME_KEY = 'emerge.reminders.chime';

/** `unsupported` is a real fourth state: iOS Safari can lack `Notification` entirely. */
export type ReminderPermission = 'granted' | 'denied' | 'default' | 'unsupported';

/**
 * Whether a sound can actually be produced yet.
 *
 * `locked` is the single most confusing failure in this whole chain: browsers
 * keep a page silent until it has seen a gesture, so a context built on mount
 * is born suspended and the first chime — the one that most needs to be heard —
 * is silent. The panel says so in as many words rather than leaving it a
 * mystery.
 */
export type ReminderAudioState = 'ready' | 'locked' | 'unsupported';

/** What a toast is announcing. `test` comes from the button, not from a task. */
export type ReminderToastVariant = 'due' | 'catch-up' | 'test';

export interface ReminderToast {
  /**
   * Unique per TOAST, not per task: the batched catch-up covers several tasks
   * and the test reminder covers none, so neither has a task id to key on.
   */
  id: string;
  /** Null for a multi-task catch-up and for the test reminder. */
  taskId: number | null;
  variant: ReminderToastVariant;
  title: string;
  /** One pre-composed line — the toast does no date work and no pluralising. */
  detail: string;
  firedAt: number;
}

export interface UseTaskRemindersOptions {
  /**
   * Called when a desktop notification is clicked. Route to `/tasks` from here.
   * The id is null when the notification covered several tasks, or none.
   */
  onActivate?: (taskId: number | null) => void;
}

export interface UseTaskRemindersResult {
  /** EXACT, from `counts.overdue` — never `overdue.length`, which is capped. */
  overdueCount: number;
  todayCount: number;
  brief: TaskBrief | null;
  loading: boolean;
  error: string | null;
  /** Master switch. Off means no polling, no timers, no chime, no notification. */
  enabled: boolean;
  setEnabled: (next: boolean) => void;
  /**
   * The chime alone, first sounding and repeat together. Toasts and
   * notifications continue when it is off.
   */
  chime: boolean;
  setChime: (next: boolean) => void;
  permission: ReminderPermission;
  /** Only ever call this from a click. Never on mount. */
  requestPermission: () => void;
  /** Whether the chime can actually be heard yet. Diagnostic, not a preference. */
  audio: ReminderAudioState;
  /**
   * Fire a reminder for a task that does not exist, down the SAME path a real
   * one takes. Touches no API and is never recorded as fired.
   */
  sendTestReminder: () => void;
  toasts: ReminderToast[];
  dismissToast: (id: string) => void;
  refresh: () => void;
}

/* ------------------------------------------------------------------ *
 * Preferences. Every storage touch is guarded — private mode throws on
 * the access itself, not merely on the write.
 * ------------------------------------------------------------------ */

function readFlag(key: string, fallback: boolean): boolean {
  try {
    const stored = localStorage.getItem(key);
    if (stored === '1') return true;
    if (stored === '0') return false;
    return fallback;
  } catch {
    return fallback;
  }
}

function writeFlag(key: string, value: boolean): void {
  try {
    localStorage.setItem(key, value ? '1' : '0');
  } catch {
    // Persisting is a convenience; the session still works without it.
  }
}

/* ------------------------------------------------------------------ *
 * The chime — Web Audio only. No audio file, no dependency.
 * ------------------------------------------------------------------ */

type AudioContextCtor = typeof AudioContext;

function audioContextCtor(): AudioContextCtor | null {
  if (typeof window === 'undefined') return null;
  const scoped = window as unknown as { webkitAudioContext?: AudioContextCtor };
  return window.AudioContext ?? scoped.webkitAudioContext ?? null;
}

/** A short, quiet two-tone. Nothing here throws into a timer callback. */
function playChime(ctx: AudioContext | null): void {
  if (!ctx) return;
  try {
    if (ctx.state === 'suspended') void ctx.resume();
    const start = ctx.currentTime + 0.02;
    // A fifth apart, rising: reads as a notification rather than as an error.
    const notes: [number, number][] = [
      [880, 0],
      [1318.5, 0.14],
    ];
    for (const [frequency, offset] of notes) {
      const oscillator = ctx.createOscillator();
      const gain = ctx.createGain();
      oscillator.type = 'sine';
      oscillator.frequency.value = frequency;
      const at = start + offset;
      // Ramped, not switched: a square-edged gain change is an audible click.
      gain.gain.setValueAtTime(0.0001, at);
      gain.gain.exponentialRampToValueAtTime(0.05, at + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, at + 0.16);
      oscillator.connect(gain).connect(ctx.destination);
      oscillator.start(at);
      oscillator.stop(at + 0.18);
    }
  } catch {
    // A machine with no working audio output must not take the reminder down
    // with it — the toast and the notification are the load-bearing halves.
  }
}

/* ------------------------------------------------------------------ *
 * Notifications. Every call is wrapped: the constructor THROWS on some
 * platforms (iOS Safari most notably) even where the API looks present.
 * ------------------------------------------------------------------ */

function normalisePermission(value: unknown): ReminderPermission {
  if (value === 'granted' || value === 'denied') return value;
  return 'default';
}

function readPermission(): ReminderPermission {
  try {
    if (typeof Notification === 'undefined') return 'unsupported';
    return normalisePermission(Notification.permission);
  } catch {
    return 'unsupported';
  }
}

/**
 * The desktop half of one delivery.
 *
 * `tag` is the de-duplication key the OS uses: keyed per task, a duplicate can
 * only ever replace the existing bubble rather than stack a second beside it.
 */
interface NotificationSpec {
  title: string;
  body: string;
  tag: string;
  /** Null when the notification covers several tasks, or none. */
  taskId: number | null;
}

function showNotification(
  spec: NotificationSpec,
  onActivate: ((taskId: number | null) => void) | undefined,
): void {
  try {
    if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return;
    const notification = new Notification(spec.title, { body: spec.body, tag: spec.tag });
    notification.onclick = () => {
      try {
        window.focus();
        notification.close();
      } catch {
        /* a browser that refuses focus() still gets the callback below */
      }
      onActivate?.(spec.taskId);
    };
  } catch {
    // Denied, unsupported, or one of the platforms that throws straight out of
    // the constructor. The in-app toast has already been queued regardless.
  }
}

/* ------------------------------------------------------------------ *
 * Brief helpers.
 * ------------------------------------------------------------------ */

/**
 * Every open, dated task in the brief, de-duplicated by id.
 *
 * `undated` is deliberately absent: a task with no due time has no instant to
 * ring at. The buckets can overlap around a boundary, so the id set is not
 * merely tidiness.
 */
function scheduledTasks(brief: TaskBrief): Task[] {
  const seen = new Set<number>();
  const out: Task[] = [];
  for (const bucket of [brief.overdue, brief.today, brief.upcoming]) {
    if (!Array.isArray(bucket)) continue;
    for (const task of bucket) {
      if (!task || task.state !== 'open' || !task.due) continue;
      if (seen.has(task.id)) continue;
      seen.add(task.id);
      out.push(task);
    }
  }
  return out;
}

/** `serverNow - localNow`. Zero when the field is unparseable — never NaN. */
function skewOf(serverNow: string): number {
  const server = Date.parse(serverNow);
  if (!Number.isFinite(server)) return 0;
  return server - Date.now();
}

function loadErrorMessage(caught: unknown): string {
  if (caught instanceof Error && caught.message) return caught.message;
  return 'Could not load your task reminders.';
}

/* ------------------------------------------------------------------ *
 * The hook.
 * ------------------------------------------------------------------ */

export function useTaskReminders(options: UseTaskRemindersOptions = {}): UseTaskRemindersResult {
  const { user } = useAuth();
  const userId = user?.id ?? null;

  const [brief, setBrief] = useState<TaskBrief | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [toasts, setToasts] = useState<ReminderToast[]>([]);
  const [nonce, setNonce] = useState(0);

  // Read during initial state, never in an effect: restoring a preference after
  // first paint shows the wrong toggle position for a frame.
  const [enabled, setEnabledState] = useState(() => readFlag(ENABLED_KEY, true));
  const [chime, setChimeState] = useState(() => readFlag(CHIME_KEY, true));
  const [permission, setPermission] = useState<ReminderPermission>(readPermission);
  // A browser with no AudioContext at all is knowable up front; everything else
  // starts locked and is promoted by the first gesture of the session.
  const [audio, setAudio] = useState<ReminderAudioState>(() =>
    audioContextCtor() ? 'locked' : 'unsupported',
  );

  /** One armed timer per task, keyed by id so a re-poll can replace it exactly. */
  const timersRef = useRef(new Map<number, ReturnType<typeof setTimeout>>());
  /**
   * THE DOUBLE-FIRE GUARD. Keyed by task id and surviving every re-poll, so a
   * task that has rung is never rung again by the next brief that still lists
   * it. The value is the `due.utc` that fired: if someone reschedules the task,
   * the stamp no longer matches and the new due time is allowed to ring once.
   */
  const firedRef = useRef(new Map<number, string>());
  /**
   * False until the first brief of a session has been reconciled.
   *
   * That first pass marks everything already past as fired WITHOUT ringing —
   * otherwise opening the app with eleven overdue tasks plays eleven chimes for
   * work that went overdue days ago, and the badge is the right surface for a
   * backlog. Only genuine backlog is swallowed, though: anything inside
   * `CATCH_UP_WINDOW_MS` is still delivered, batched, by `fireCatchUp`. Reset
   * when reminders are switched off, so turning them back on does not replay
   * the gap.
   */
  const seededRef = useRef(false);
  const skewRef = useRef(0);
  const audioRef = useRef<AudioContext | null>(null);
  const chimeRef = useRef(chime);
  const zoneRef = useRef(viewerZone(user?.timezone));
  const onActivateRef = useRef(options.onActivate);
  const mountedRef = useRef(true);

  /**
   * THE REPEAT LEDGER: live toast id -> the instant its SOUND expires.
   *
   * Keyed by toast id rather than task id because the batched catch-up and the
   * test reminder have no task id, and because dismissal and `MAX_TOASTS`
   * eviction both speak in toast ids. An entry here is the only thing keeping
   * the ticker below alive, so pruning it is how every timer gets cleaned up.
   */
  const chimeUntilRef = useRef(new Map<string, number>());
  /**
   * ONE ticker for every live reminder, deliberately — not one per toast.
   *
   * Three tasks falling due together is three reminders that each want to
   * chime every twenty seconds. Three independent timers would drift into each
   * other and produce a burst of overlapping tones; staggering them by a fixed
   * offset only moves the collision around and gets worse as the count grows.
   * A single shared interval CANNOT overlap with itself: whatever is on screen,
   * the tab makes exactly one chime per tick.
   */
  const repeatTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Refs the fire path reads. Kept fresh here rather than in the polling
  // effect's deps, so flipping the chime does not tear down live timers.
  useEffect(() => {
    chimeRef.current = chime;
    zoneRef.current = viewerZone(user?.timezone);
    onActivateRef.current = options.onActivate;
  });

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  /*
   * Derived, not stored. A `loading` flag would have to be set from inside the
   * effect that starts the fetch — the setState-in-effect this codebase avoids
   * — and it would have to be cleared again on every disable.
   */
  const loading = enabled && userId !== null && brief === null && error === null;

  const setEnabled = useCallback((next: boolean) => {
    setEnabledState(next);
    writeFlag(ENABLED_KEY, next);
    // Turning reminders off clears what is on screen too: leaving a toast up
    // for a system the user has just switched off is a contradiction.
    if (!next) setToasts([]);
  }, []);

  const setChime = useCallback((next: boolean) => {
    setChimeState(next);
    writeFlag(CHIME_KEY, next);
  }, []);

  const stopRepeat = useCallback(() => {
    if (repeatTimerRef.current === null) return;
    clearInterval(repeatTimerRef.current);
    repeatTimerRef.current = null;
  }, []);

  /** One tick of the shared ticker: at most one chime, however many are live. */
  const repeatTick = useCallback(() => {
    const now = Date.now();
    let live = false;
    for (const until of chimeUntilRef.current.values()) {
      if (until > now) {
        live = true;
        break;
      }
    }
    // Everything has been dismissed or has run past its limit: the ticker takes
    // itself down rather than spinning on with nothing left to announce.
    if (!live) {
      stopRepeat();
      return;
    }
    // Re-read the mute preference every tick. Muting silences the repeat, and
    // the repeat can never resurrect a chime the user switched off.
    if (chimeRef.current) playChime(audioRef.current);
  }, [stopRepeat]);

  const dismissToast = useCallback((id: string) => {
    // Before the re-render, on purpose: dismissing stops THAT reminder's chime
    // immediately rather than at the next tick.
    chimeUntilRef.current.delete(id);
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  /**
   * THE ONE DELIVERY PATH — toast, chime, notification, in that order.
   *
   * A task falling due, the batched catch-up and the test reminder all come
   * through here, deliberately: a test that took its own route would prove
   * nothing about the route a real reminder takes.
   */
  const deliver = useCallback((toast: ReminderToast, notification: NotificationSpec) => {
    // The toast is unconditional — it is the fallback for every notification
    // state, including the denied one that can never be re-asked.
    setToasts((prev) =>
      [...prev.filter((item) => item.id !== toast.id), toast].slice(-MAX_TOASTS),
    );
    /*
     * Arm the repeat BEFORE the first chime, and unconditionally — even while
     * muted. The ledger is a property of the TOAST, not of the sound: unmuting
     * halfway through picks the repeat up where it is rather than leaving a
     * visible reminder permanently silent. Re-delivering the same id (a
     * rescheduled task, a second click of the test button) resets the window,
     * which is exactly what replacing the toast means.
     */
    chimeUntilRef.current.set(
      toast.id,
      Date.now() +
        (toast.variant === 'test' ? TEST_CHIME_REPEAT_LIMIT_MS : CHIME_REPEAT_LIMIT_MS),
    );
    if (chimeRef.current) playChime(audioRef.current);
    showNotification(notification, onActivateRef.current);
  }, []);

  /**
   * Fire a reminder for a task that does not exist.
   *
   * Purely diagnostic, and the diagnosis only works because it is the same
   * `deliver` a real reminder uses: one click says whether the toast, the chime
   * and the desktop notification are each alive. It calls no API and is never
   * written to `firedRef`, so it can neither create work nor suppress a real
   * task. The mute toggle still governs the SOUND — a muted user should see the
   * toast prove itself rather than be told nothing happened.
   */
  const sendTestReminder = useCallback(() => {
    const firedAt = Date.now();
    deliver(
      {
        // A fixed id, so a second click replaces the first rather than stacking
        // test toasts over a real reminder.
        id: 'test',
        taskId: null,
        variant: 'test',
        title: 'Test reminder',
        detail: `Not a real task · sent ${new Date(firedAt).toLocaleTimeString()}`,
        firedAt,
      },
      {
        title: 'Test reminder — SEED EMERGE',
        body: 'This is what a task reminder looks like. No task is actually due.',
        tag: 'emerge-task-test',
        taskId: null,
      },
    );
  }, [deliver]);

  const refresh = useCallback(() => setNonce((value) => value + 1), []);

  /**
   * Ask for notification permission. **Only from a user gesture.**
   *
   * A prompt on page load is the thing browsers and users both hate, and once
   * it is denied it can never be asked again — there is no API to re-prompt,
   * which is a browser rule rather than something to work around.
   */
  const requestPermission = useCallback(() => {
    const settle = (result: unknown) => {
      if (mountedRef.current) setPermission(normalisePermission(result));
    };

    let request: Promise<NotificationPermission> | void;
    try {
      if (typeof Notification === 'undefined') {
        setPermission('unsupported');
        return;
      }
      // The callback form is passed as well as the promise handled: older
      // Safari resolves only the callback and returns nothing at all.
      request = Notification.requestPermission(settle);
    } catch {
      setPermission('unsupported');
      return;
    }

    if (request && typeof request.then === 'function') {
      request.then(settle).catch(() => {
        if (mountedRef.current) setPermission(readPermission());
      });
    }
  }, []);

  /*
   * THE ONE PLACE THE TICKER IS STARTED AND STOPPED.
   *
   * Driven off `toasts`, so every way a toast can leave — dismissed, evicted by
   * `MAX_TOASTS`, cleared by switching reminders off — prunes the ledger
   * through the same line. There is no per-toast timer for any of those paths
   * to forget.
   *
   * Deliberately returns NO cleanup: re-running as each new toast arrives must
   * not tear the interval down and rebuild it, or the cadence would restart
   * from zero every time another task fell due. Unmount is handled by the
   * effect below, and `repeatTick` stops itself once nothing is live.
   */
  useEffect(() => {
    const ledger = chimeUntilRef.current;
    const onScreen = new Set(toasts.map((toast) => toast.id));
    for (const id of [...ledger.keys()]) {
      if (!onScreen.has(id)) ledger.delete(id);
    }

    const now = Date.now();
    let live = false;
    for (const until of ledger.values()) {
      if (until > now) {
        live = true;
        break;
      }
    }

    // Muted means no sound at all, so the ticker does not even run.
    if (!live || !chime) {
      stopRepeat();
      return;
    }
    if (repeatTimerRef.current === null) {
      repeatTimerRef.current = setInterval(repeatTick, CHIME_REPEAT_MS);
    }
  }, [toasts, chime, repeatTick, stopRepeat]);

  /*
   * The unmount guarantee. An interval left chiming with no toast on screen is
   * the worst outcome this file can produce, so it gets an effect of its own
   * rather than sharing one whose dependencies change.
   */
  useEffect(() => {
    const ledger = chimeUntilRef.current;
    return () => {
      stopRepeat();
      ledger.clear();
    };
  }, [stopRepeat]);

  /*
   * Unlock the AudioContext on the FIRST gesture of the session.
   *
   * Browsers refuse to start audio before an interaction, so a context created
   * on mount is born suspended and the first chime is silent — the one chime
   * that most needs to be heard. Both listeners are one-shot and each removes
   * the other. This runs whatever the chime preference is: someone who unmutes
   * an hour in has no second first-gesture to spend.
   *
   * It also reports what happened into `audio`, because "the chime never
   * played" and "the browser has not let this page make a sound yet" are the
   * same symptom with completely different fixes.
   */
  useEffect(() => {
    function detach() {
      window.removeEventListener('pointerdown', unlock, true);
      window.removeEventListener('keydown', unlock, true);
    }

    function settle(state: ReminderAudioState) {
      if (mountedRef.current) setAudio(state);
    }

    function unlock() {
      detach();
      if (!audioRef.current) {
        const Ctor = audioContextCtor();
        if (!Ctor) {
          settle('unsupported');
          return;
        }
        try {
          audioRef.current = new Ctor();
        } catch {
          audioRef.current = null;
          settle('unsupported');
          return;
        }
      }
      const ctx = audioRef.current;
      if (!ctx) return;
      if (ctx.state !== 'suspended') {
        settle('ready');
        return;
      }
      try {
        const resuming = ctx.resume();
        // Older Safari returns nothing at all from resume() rather than a promise.
        if (resuming && typeof resuming.then === 'function') {
          resuming.then(
            () => settle('ready'),
            () => settle('locked'),
          );
        } else {
          settle('ready');
        }
      } catch {
        /* the chime is a nicety; the toast is the reminder */
      }
    }

    window.addEventListener('pointerdown', unlock, true);
    window.addEventListener('keydown', unlock, true);

    return () => {
      detach();
      const ctx = audioRef.current;
      audioRef.current = null;
      try {
        void ctx?.close();
      } catch {
        /* already closed, or a context that never opened */
      }
    };
  }, []);

  /*
   * Poll, schedule, fire. One effect owns every timer, every listener and the
   * in-flight request, so a sign-out or a disable takes all of them down
   * together — a leaked interval polling a shared, rate-limited API is a real
   * hazard here, not a theoretical one.
   */
  useEffect(() => {
    if (!enabled || userId === null) return;

    const timers = timersRef.current;
    const fired = firedRef.current;

    let disposed = false;
    let inflight: AbortController | null = null;
    let pollTimer: ReturnType<typeof setTimeout> | undefined;
    let lastLoadAt = 0;
    let failures = 0;

    const clearTaskTimers = () => {
      for (const timer of timers.values()) clearTimeout(timer);
      timers.clear();
    };

    /** One task's own due line, rendered through the viewer's zone. */
    const detailOf = (task: Task) =>
      task.due
        ? `${taskKindLabel(task.kind)} · due ${formatDue(task.due, zoneRef.current)}`
        : taskKindLabel(task.kind);

    const fire = (task: Task) => {
      if (disposed || !task.due) return;
      fired.set(task.id, task.due.utc);

      const detail = detailOf(task);
      deliver(
        {
          id: `task:${task.id}`,
          taskId: task.id,
          variant: 'due',
          title: task.title,
          detail,
          firedAt: Date.now(),
        },
        {
          title: `Task due — ${task.title}`,
          body: detail,
          tag: `emerge-task-${task.id}`,
          taskId: task.id,
        },
      );
    };

    /**
     * Everything that fell due while nobody was watching, in ONE delivery.
     *
     * Batched on purpose. The tasks are already recorded in `fired` by the
     * caller, so this is the only chance they will ever get to be mentioned —
     * and mentioning them one at a time would be the eleven-chime problem all
     * over again in miniature. One toast, at most one chime, one notification,
     * whatever the count.
     */
    const fireCatchUp = (tasks: Task[]) => {
      if (disposed || tasks.length === 0) return;

      const firedAt = Date.now();
      const only = tasks.length === 1 ? tasks[0] : null;

      if (only) {
        const detail = detailOf(only);
        deliver(
          {
            id: `catch-up:${firedAt}`,
            taskId: only.id,
            variant: 'catch-up',
            title: only.title,
            detail,
            firedAt,
          },
          {
            title: `Task due — ${only.title}`,
            body: detail,
            tag: 'emerge-task-catch-up',
            taskId: only.id,
          },
        );
        return;
      }

      const headline = `${tasks.length} tasks came due while you were away`;
      const titles = tasks.map((task) => task.title);
      // A notification body has very little room, and a tray bubble ellipsised
      // mid-title tells the reader less than a plain count does.
      const detail =
        titles.length > 3
          ? `${titles.slice(0, 3).join(' · ')} · and ${titles.length - 3} more`
          : titles.join(' · ');

      deliver(
        {
          id: `catch-up:${firedAt}`,
          taskId: null,
          variant: 'catch-up',
          title: headline,
          detail,
          firedAt,
        },
        { title: headline, body: detail, tag: 'emerge-task-catch-up', taskId: null },
      );
    };

    const reconcile = (data: TaskBrief) => {
      // Every timer is re-derived from the brief just received: a task that was
      // completed, reassigned or rescheduled must not keep the timer armed from
      // the version of it we saw ninety seconds ago.
      clearTaskTimers();

      const seeding = !seededRef.current;
      seededRef.current = true;
      const skew = skewRef.current;
      const live = new Set<number>();
      /** Seeding only: what went overdue recently enough to still be worth saying. */
      const cameDue: Task[] = [];

      for (const task of scheduledTasks(data)) {
        const due = task.due;
        if (!due) continue;
        live.add(task.id);

        // The id is the reconciliation key; the stamp lets a RESCHEDULED task
        // ring again on its new time.
        if (fired.get(task.id) === due.utc) continue;

        const at = Date.parse(due.utc);
        if (!Number.isFinite(at)) continue;

        // The whole point of the skew: the due instant is on the server's
        // timeline and `Date.now()` is on this laptop's.
        const delay = at - skew - Date.now();

        if (delay <= 0) {
          if (seeding) {
            // Already past when the session opened. Recorded either way — the
            // fired map is the double-fire guard, and a task surfaced by the
            // catch-up below must never ring again on the next poll.
            fired.set(task.id, due.utc);
            // A genuine backlog stays silent. Something that fell due in the
            // last few minutes, while the tab was shut, is a reminder that was
            // MISSED rather than one that is old, so it still gets said.
            if (-delay <= CATCH_UP_WINDOW_MS) cameDue.push(task);
            continue;
          }
          fire(task);
          continue;
        }

        // Beyond the setTimeout ceiling the browser fires IMMEDIATELY, so this
        // one is left unscheduled; a later poll arms it once it is inside the
        // cap. Re-arming on the poll is the whole reason the poll exists.
        if (delay > MAX_TIMEOUT_MS) continue;

        timers.set(
          task.id,
          setTimeout(() => {
            timers.delete(task.id);
            fire(task);
          }, delay),
        );
      }

      /*
       * Forget tasks that have left the brief — closed, cancelled, reassigned —
       * so a long session does not grow the map without bound. Skipped when the
       * response is `truncated`: an absence there means "past the cap", not
       * "gone", and forgetting one would let it ring a second time.
       */
      if (!data.truncated) {
        for (const id of [...fired.keys()]) {
          if (!live.has(id)) fired.delete(id);
        }
      }

      // Last, and only ever populated on the seeding pass. Oldest first, so the
      // batch reads in the order the tasks actually came due.
      if (cameDue.length > 0) {
        cameDue.sort((a, b) => (a.due?.utc ?? '').localeCompare(b.due?.utc ?? ''));
        fireCatchUp(cameDue);
      }
    };

    const queueNextPoll = () => {
      clearTimeout(pollTimer);
      const base = POLL_MIN_MS + Math.random() * (POLL_MAX_MS - POLL_MIN_MS);
      // Backing off a failing endpoint rather than hammering it: a 429 answered
      // at the same cadence is how a shared office IP stays limited.
      const delay =
        failures === 0 ? base : Math.min(base * 2 ** Math.min(failures, 3), MAX_BACKOFF_MS);
      pollTimer = setTimeout(load, delay);
    };

    function load() {
      inflight?.abort();
      const controller = new AbortController();
      inflight = controller;
      lastLoadAt = Date.now();

      api
        .taskBrief(BRIEF_PARAMS, controller.signal)
        .then((response) => {
          if (disposed || controller.signal.aborted) return;
          failures = 0;
          // Before anything is compared or scheduled.
          skewRef.current = skewOf(response.data.now);
          setBrief(response.data);
          setError(null);
          reconcile(response.data);
        })
        .catch((caught: unknown) => {
          if (disposed || controller.signal.aborted) return;
          failures += 1;
          // The last good brief stays underneath: a failed refresh is not a
          // reason to blank a badge that was right a minute ago.
          setError(loadErrorMessage(caught));
        })
        .finally(() => {
          if (disposed || controller.signal.aborted) return;
          queueNextPoll();
        });
    }

    /*
     * Background tabs get their timers throttled hard, and a sleeping laptop
     * stops them altogether — so coming back to a tab that has been away is its
     * own trigger, not something the interval can be trusted to cover.
     */
    const onVisibility = () => {
      if (document.visibilityState !== 'visible') return;
      if (Date.now() - lastLoadAt < REFRESH_AFTER_HIDDEN_MS) return;
      load();
    };

    load();
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      disposed = true;
      document.removeEventListener('visibilitychange', onVisibility);
      clearTimeout(pollTimer);
      inflight?.abort();
      clearTaskTimers();
      // A fresh seed next time round, so re-enabling does not replay the gap.
      seededRef.current = false;
    };
  }, [enabled, userId, nonce, deliver]);

  return {
    // EXACT counts, from the server's own query. `overdue.length` is the capped
    // list and would under-report the moment the day got bad enough to matter.
    overdueCount: brief?.counts.overdue ?? 0,
    todayCount: brief?.counts.today ?? 0,
    brief,
    loading,
    error,
    enabled,
    setEnabled,
    chime,
    setChime,
    permission,
    requestPermission,
    audio,
    sendTestReminder,
    toasts,
    dismissToast,
    refresh,
  };
}
